import { computed, ref, reactive, watch, type Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import {
  ShopListInput,
  ListShopSearchQuery,
  ListShopSearchDocument,
  ListShopSearchQueryVariables,
} from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { displayPagingRange as _displayPagingRange } from "@/common/common";
import type { ListData, Header, ShopListTableParams } from "../types";

/**
 * 一覧テーブル表示に必要なデータ整形と列情報を提供する composable。
 *
 * @returns テーブル描画に利用する列定義と表示用データ
 */
export const useShopListTable = (params: ShopListTableParams) => {
  const {
    searchConditionChainCode,
    searchConditionShopCode,
    searchConditionPhone,
    searchConditionZipCode,
    searchConditionAddress,
    ShopNameSelectValues,
    chainViewFlag,
    errdialog,
    errMessage,
  } = params;

  /* 一覧表示 */
  const tableHeaders = ref<Header[]>([]);
  const listLoading = ref(false);

  const listOptions = reactive({
    page: 1,
    itemsPerPage: 10,
    sortBy: [] as string[],
    sortDesc: [] as boolean[],
    groupBy: [] as string[],
    groupDesc: [] as boolean[],
    multiSort: false,
    mustSort: false,
  });

  //ページング範囲の表示
  const displayPagingRange = ref("");

  // 店舗一覧 データ
  const listDataArray = ref<ListData[]>([]);

  // 店舗一覧 絞込み後の全データ数
  const listDataTotalCount = ref(0);

  // 店舗一覧 ページ数
  const pageCount = computed(() =>
    Math.max(
      1,
      Math.ceil(listDataTotalCount.value / (listOptions.itemsPerPage || 1))
    )
  );

  // 選択されたチェックボックス
  const selectListCheckbox = ref<ListData[]>([]);

  // 検索ボタンからの呼び出し時にwatchをスキップするフラグ
  const isSearchButtonClick = ref(false);

  /**
   * 初期処理（テーブルヘッダー構築）
   */
  const initializeTableHeaders = (): void => {
    if (chainViewFlag) {
      tableHeaders.value = [
        {
          title: "チェーン",
          align: "center",
          sortable: false,
          key: "chain_code",
        },
      ];
    }
    tableHeaders.value.push(
      {
        title: "店舗コード",
        align: "center",
        sortable: false,
        key: "shop_code",
      },
      {
        title: "店舗名",
        align: "center",
        sortable: false,
        key: "shop_name",
      },
      {
        title: "郵便番号",
        align: "center",
        sortable: false,
        key: "zip_code",
      },
      {
        title: "住所",
        align: "left",
        sortable: false,
        key: "address",
      },
      {
        title: "TEL",
        align: "center",
        sortable: false,
        key: "tel",
      },
      {
        title: "",
        align: "center",
        sortable: false,
        key: "actions",
      }
    );
  };

  /**
   * 絞り込み検索 @Click
   */
  const onClickSearch = async (): Promise<void> => {
    // 検索ボタンをクリックした場合、ページを1に戻す（既に1ページ目の場合はスキップ）
    if (listOptions.page !== 1) {
      isSearchButtonClick.value = true;
      listOptions.page = 1;
    }

    //検索する
    await searchQuery();
  };

  /**
   * 絞り込み検索
   */
  const searchQuery = async (): Promise<void> => {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      // 店舗一覧 input [limit 取得件数 offset:取得開始位置]
      //検索条件の店舗コード・店舗名を配列にまとめる
      let shop_list_code: string[] = [];
      if (
        searchConditionShopCode.value !== undefined &&
        searchConditionShopCode.value !== null &&
        searchConditionShopCode.value !== ""
      ) {
        shop_list_code = [searchConditionShopCode.value];
      }

      if (
        ShopNameSelectValues.value !== undefined &&
        ShopNameSelectValues.value !== null &&
        ShopNameSelectValues.value.length > 0
      ) {
        ShopNameSelectValues.value.forEach((value) => {
          if (value.shop_code !== "") {
            if (shop_list_code == undefined || shop_list_code == null) {
              shop_list_code = [value.shop_code];
            } else {
              shop_list_code.push(value.shop_code);
            }
          }
        });
      }

      // 一覧検索条件
      const listInput: ShopListInput = {
        shop_list:
          shop_list_code !== undefined &&
          shop_list_code !== null &&
          shop_list_code.length > 0
            ? shop_list_code
            : null,
        phone:
          searchConditionPhone.value !== undefined &&
          searchConditionPhone.value !== null &&
          searchConditionPhone.value !== ""
            ? searchConditionPhone.value
            : "",
        zip:
          searchConditionZipCode.value !== undefined &&
          searchConditionZipCode.value !== null &&
          searchConditionZipCode.value !== ""
            ? searchConditionZipCode.value
            : "",
        address:
          searchConditionAddress.value !== undefined &&
          searchConditionAddress.value !== null &&
          searchConditionAddress.value !== ""
            ? searchConditionAddress.value
            : "",
        chain_code:
          chainViewFlag &&
          searchConditionChainCode.value !== undefined &&
          searchConditionChainCode.value !== null &&
          searchConditionChainCode.value !== ""
            ? searchConditionChainCode.value
            : localStorageGetChainCode(),
        customer_code: localStorageGetCustomerCode(),
        limit: listOptions.itemsPerPage,
        offset: (listOptions.page - 1) * listOptions.itemsPerPage,
      };

      //件数取得用
      const CountInput = {
        shop_list:
          shop_list_code !== undefined &&
          shop_list_code !== null &&
          shop_list_code.length > 0
            ? shop_list_code
            : null,
        phone:
          searchConditionPhone.value !== undefined &&
          searchConditionPhone.value !== null &&
          searchConditionPhone.value !== ""
            ? searchConditionPhone.value
            : "",
        zip:
          searchConditionZipCode.value !== undefined &&
          searchConditionZipCode.value !== null &&
          searchConditionZipCode.value !== ""
            ? searchConditionZipCode.value
            : "",
        address:
          searchConditionAddress.value !== undefined &&
          searchConditionAddress.value !== null &&
          searchConditionAddress.value !== ""
            ? searchConditionAddress.value
            : "",
        chain_code:
          chainViewFlag &&
          searchConditionChainCode.value !== undefined &&
          searchConditionChainCode.value !== null &&
          searchConditionChainCode.value !== ""
            ? searchConditionChainCode.value
            : localStorageGetChainCode(),
        customer_code: localStorageGetCustomerCode(),
      };

      // 店舗一覧取得
      let result: ApolloQueryResult<ListShopSearchQuery>;
      try {
        result = await apolloClient.query<
          ListShopSearchQuery,
          ListShopSearchQueryVariables
        >({
          query: ListShopSearchDocument,
          variables: {
            listInput: listInput,
            countInput: CountInput,
          },
          fetchPolicy: "no-cache",
        });
      } catch (err: unknown) {
        console.error(err);
        errdialog.value = true;
        if (err instanceof Error) {
          errMessage.value = "店舗一覧データの取得に失敗しました。";
        } else {
          errMessage.value =
            "店舗一覧データの取得に失敗しました。（不明なエラー）";
        }
        return;
      }

      if (
        result.data.listShop === undefined ||
        result.data.listShop === null ||
        result.data.getShopCount === undefined ||
        result.data.getShopCount === null
      ) {
        if (
          result.data.listShop === undefined ||
          result.data.listShop === null
        ) {
          // 店舗一覧取得 エラー
          console.error("result.data.listShop is undefined or null");
          errdialog.value = true;
          errMessage.value = "店舗一覧データの取得に失敗しました。";
          return;
        }
        if (
          result.data.getShopCount === undefined ||
          result.data.getShopCount === null
        ) {
          // 店舗件数 エラー
          console.error("result.data.getShopCount is undefined or null");
          errdialog.value = true;
          errMessage.value = "店舗一覧データの取得に失敗しました。";
          return;
        }
      }

      // 店舗一覧のデータを作成
      listDataArray.value = [];
      const list = result.data.listShop;
      list.forEach((value) => {
        if (value !== undefined && value !== null) {
          listDataArray.value.push({
            chain_code: value.chain_code,
            shop_code: value.shop_code,
            shop_name: value.name,
            zip_code: value.zip_code,
            address:
              value.address3 !== undefined &&
              value.address3 !== null &&
              value.address3 !== ""
                ? value.address1 + value.address2 + value.address3
                : value.address1 + value.address2,
            tel:
              value.phone !== undefined &&
              value.phone !== null &&
              value.phone !== ""
                ? value.phone
                : "",
          });
        }
      });

      // 絞込み後の全データ数 更新
      listDataTotalCount.value = result.data.getShopCount.count;

      // ページングの表示
      displayPagingRange.value = _displayPagingRange(
        listOptions.page - 1,
        listOptions.itemsPerPage,
        listDataTotalCount.value
      );
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "予期しないエラーが発生しました。";
      }
      return;
    }
  };

  // listOptions変化時の検索
  watch(
    () => ({ ...listOptions }),
    async () => {
      // 検索ボタンからの呼び出し時はスキップ（onClickSearchで直接処理）
      if (isSearchButtonClick.value) {
        isSearchButtonClick.value = false;
        return;
      }
      listLoading.value = true;
      await searchQuery();
      listLoading.value = false;
    },
    { deep: true }
  );

  return {
    tableHeaders,
    listLoading,
    listOptions,
    displayPagingRange,
    listDataArray,
    listDataTotalCount,
    pageCount,
    selectListCheckbox,
    isSearchButtonClick,
    initializeTableHeaders,
    onClickSearch,
    searchQuery,
  };
};
